import React from "react";
import {
    Box, InputAdornment, Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import AddRoundedIcon                from "@mui/icons-material/AddRounded";
import SearchRoundedIcon             from "@mui/icons-material/SearchRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import "dayjs/locale/ko";
import { TAB_EMPTY_MSG, TAB_STATUS_FILTERS } from "./approvalConstants";
import { ds } from "../../styles/designSystem";
import LoadingDots from "../../component/common/LoadingDots";
import TablePaginator, { TableSizeSelect, DEFAULT_PAGE_SIZE } from "../../component/common/TablePaginator";
import ActionButton from "../../component/common/ActionButton";
import ComboBox from "../../component/common/ComboBox";
import ApprovalTable from "./ApprovalTable";

/* ── ApprovalNormalPanel — 표 형식 목록 + 탭 + 드롭다운 필터 ─── */
export default function ApprovalNormalPanel({
    tab,
    setTab,
    approvals,
    loading,
    filtered,
    statusFilter,
    setStatusFilter,
    submitterFilter,
    setSubmitterFilter,
    typeFilter,
    formOptions = [],
    setTypeFilter,
    selected,
    onSelect,
    isAdmin,
    onEnterAdmin,
    onOpenCreate,
    viewingUnread = 0,
    page = 1, pageSize = DEFAULT_PAGE_SIZE, totalPages = 1, totalElements = 0,
    onPageChange, onPageSizeChange,
}) {
    const statusCount = (value) => approvals.filter((a) => a.status === value).length;

    const anyFilter = statusFilter !== "ALL" || typeFilter !== "ALL" || (submitterFilter || "").trim();
    const emptyText = anyFilter ? "조건에 맞는 결재가 없습니다" : TAB_EMPTY_MSG[tab];

    const ddSx = (w) => ({ width: w, flexShrink: 0 });

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* 헤더 */}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>결재</Typography>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.2 }}>결재 문서를 작성·조회하고 승인/반려를 처리합니다.</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    {isAdmin && (
                        <ActionButton variant="secondary"
                            startIcon={<AdminPanelSettingsRoundedIcon sx={{ fontSize: 14 }} />}
                            onClick={onEnterAdmin}
                            label="관리자 모드"
                            sx={{ fontSize: 12, py: 0.4, color: "#2563eb", borderColor: "#93c5fd",
                                "&:hover": { bgcolor: "#eff6ff", borderColor: "#2563eb" } }} />
                    )}
                    <ActionButton variant="primary"
                        startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={onOpenCreate}
                        label="올리기" />
                </Stack>
            </Stack>

            <Box sx={{ borderTop: "1px solid #e2e8f0", pt: 1 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={ds.components.tabs.compact} style={{paddingBottom: 10}}>
                    <Tab label="전체 보기" />
                    <Tab label="받은 결재" />
                    <Tab label="처리 완료" />
                    <Tab label="올린 결재" />
                    <Tab label={viewingUnread > 0 ? `열람 결재 (${viewingUnread})` : "열람 결재"} />
                </Tabs>
                {/* 필터 바 — 한 줄에 모은다. 예전엔 «N개씩 보기» 만 윗줄에 따로 떠서
                    같은 성격의 조작이 두 줄로 갈라져 있었다.
                    검색은 오른쪽 끝에 붙인다(가변 폭이라 창을 넓히면 검색칸이 늘어난다). */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.25 }} flexWrap="wrap" rowGap={1}>
                    <Box sx={ddSx(128)}>
                        <ComboBox clearable={false} placeholder="상태"
                            options={TAB_STATUS_FILTERS[tab].map((f) => ({
                                value: f.value,
                                label: `${f.label}${f.value !== "ALL" ? ` (${statusCount(f.value)})` : ""}`,
                            }))}
                            value={statusFilter}
                            onChange={(v) => setStatusFilter(v)} />
                    </Box>

                    <Box sx={ddSx(200)}>
                        <ComboBox clearable={false} placeholder="결재유형"
                            options={[{ value: "ALL", label: "전체" }, ...formOptions]}
                            value={typeFilter}
                            onChange={(v) => setTypeFilter(v)} />
                    </Box>

                    {tab !== 1 && (
                        <Box sx={{ flexShrink: 0 }}>
                            <TableSizeSelect value={pageSize} onChange={onPageSizeChange} />
                        </Box>
                    )}

                    <Box sx={{ flex: 1 }} />

                    <TextField size="small" value={submitterFilter}
                        onChange={(e) => setSubmitterFilter(e.target.value)}
                        placeholder="기안자 이름/부서 검색"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ fontSize: 17, color: "#94a3b8" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }} />
                </Stack>

                {loading
                    ? <LoadingDots />
                    : <ApprovalTable rows={filtered} selected={selected} onSelect={onSelect} emptyText={emptyText} />}
                {/* 받은 결재(탭1)는 «지금 내 차례» 라 페이지가 없다 — 페이지네이터도 숨긴다. */}
                {tab !== 1 && (
                    <TablePaginator
                        totalPages={totalPages}
                        total={totalElements}
                        pageSize={pageSize}
                        page={page}
                        onPageChange={onPageChange}
                    />
                )}
            </Box>
        </Box>
    );
}
